import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import info from "../assets/images/building.webp";
import Building from "../assets/images/building.webp";
import Building2 from "../assets/images/buildingconstruct.webp";
import Const from "../assets/images/const.webp";
import Dummy from "../assets/images/road.webp";
import WaterSupply from "../assets/images/WaterSupply.webp";
import WaterTreat from "../assets/images/Watertreat.webp";
import DrainC from "../assets/images/SWDC.webp";
import Well from "../assets/images/intakewell.webp";
import UnderSewage from "../assets/images/undergroundsewage.webp";


const animations = {
  fadeInUp: {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { 
        duration: 0.6,
        ease: "easeOut"
      },
    },
  },
  fadeInLeft: {
    hidden: { opacity: 0, x: -60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { 
        duration: 0.7,
        ease: "easeInOut"
      },
    },
  },
  fadeInRight: {
    hidden: { opacity: 0, x: 60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { 
        duration: 0.7,
        ease: "easeInOut"
      },
    },
  },
  scaleIn: {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { 
        duration: 0.5,
        ease: "easeOut"
      },
    },
  },
};

const OptimizedImage = React.memo(({ src, alt, className }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const blurDataURL = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4gHYSUNDX1BST0ZJTEUAAQEAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADb/2wBDABQODxIPDRQSEBIXFRQdHx4eHRoaHSQtJiEwMS4qLy4yMjY6QUI/SkZMNkJHUFVkZGR/bX9qcXp8f3z/2wBDAR";

  return (
    <div ref={ref} className={`${className} relative overflow-hidden`}>
      {!isLoaded && !error && inView && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      )}
      {inView && (
        <img
          src={src}
          alt={alt}
          className={`${className} ${
            isLoaded ? "opacity-100" : "opacity-0"
          } transition-opacity duration-300`}
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
          onError={() => setError(true)}
          decoding="async"
          style={{
            backgroundImage: `url(${blurDataURL})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
      )}
    </div>
  );
});

// Service Card component for the ServiceLayout section
const ServiceCard = React.memo(({ service, className }) => {
  return (
    <div
      className={`relative overflow-hidden rounded-lg shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${className}`}
    >
      <OptimizedImage
        src={service.image}
        alt={service.alt}
        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
          <h3 className="text-white text-lg md:text-xl font-medium leading-snug mb-2">
            {service.title}
          </h3>
        </div>
      </div>
    </div>
  );
});

const useCarousel = (images, interval = 10000) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const nextImage = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
      setIsVisible(true);
    }, 70);
  }, [images.length]);

  useEffect(() => {
    let rafId;
    let lastTime = 0;
    
    const animate = (timestamp) => {
      if (timestamp - lastTime >= interval) {
        nextImage();
        lastTime = timestamp;
      }
      rafId = requestAnimationFrame(animate);
    };
    
    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [interval, nextImage]);

  return [currentIndex, isVisible];
};

const HeroSection = React.memo(() => {
  const images = useMemo(
    () => [Building, Building2, Const, Dummy],
    []
  );

  const [currentImageIndex, isVisible] = useCarousel(images);
  const [bgLoaded, setBgLoaded] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = images[currentImageIndex];
    img.onload = () => setBgLoaded(true);
  }, [currentImageIndex, images]);

  return (
    
      <motion.div
        key={currentImageIndex}
        initial={{ opacity: 0 }}
        animate={{ opacity: isVisible && bgLoaded ? 1 : 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full h-[90vh] md:h-screen flex justify-center items-center bg-cover bg-center bg-no-repeat text-white text-center relative"
        style={{
          backgroundImage: `url(${images[currentImageIndex]})`
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative z-10 max-w-4xl px-4 md:px-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl mb-4 font-bold leading-tight drop-shadow-lg">
            Building today for a sustainable tomorrow.
          </h1>
          <p className="text-base md:text-lg lg:text-xl mb-6 md:mb-8 px-2 md:px-4 drop-shadow-lg">
            We provide high-quality engineering and project management solutions,
            focusing on safety, efficiency, and cost-effectiveness.
          </p>
          <div className="space-x-2 md:space-x-4">
            <Link to="/Contact">
              <button className="text-sm md:text-base bg-[#87212e] text-white px-4 md:px-6 py-2 md:py-3 rounded-full hover:bg-[#6d1a25] transition-colors">
                Get A Quote
              </button>
            </Link>
            <Link to="/Leadership">
              <button className="text-sm md:text-base border-2 border-white text-white px-4 md:px-6 py-2 md:py-3 rounded-full hover:bg-white/10 transition-colors">
                More About Us
              </button>
            </Link>
          </div>
        </div>
      </motion.div>
    
  );
});

const PreInfoHome = React.memo(() => {
  const { ref: statsRef, inView: statsInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  const { ref: imageRef, inView: imageInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  const { ref: textRef, inView: textInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <div className="w-full py-8 md:py-16 px-4">
      <motion.div
        ref={statsRef}
        className="flex flex-col md:flex-row justify-center gap-6 md:gap-12 mb-12 md:mb-16"
        initial="hidden"
        animate={statsInView ? "visible" : "hidden"}
        variants={animations.fadeInUp}
      >
      </motion.div>

      <div className="flex flex-col lg:flex-row items-center gap-8 px-4 md:px-8 lg:px-20">
        <motion.div
          ref={imageRef}
          className="relative flex-1 w-full lg:w-auto"
          initial="hidden"
          animate={imageInView ? "visible" : "hidden"}
          variants={animations.fadeInLeft}
        >
          <OptimizedImage
            src={info}
            alt="Infrastructure"
            className="w-full rounded-lg shadow-lg"
          />
          <motion.div
            className="absolute -bottom-4 right-0 md:-bottom-8 md:-right-16 bg-[#87212e] text-white p-6 md:p-10 rounded-lg text-center"
            variants={animations.scaleIn}
          >
            <h3 className="text-3xl md:text-4xl font-bold">
              30<span>+</span>
            </h3>
            <p className="mt-2 text-sm md:text-base">Years of experience</p>
          </motion.div>
        </motion.div>

        <motion.div
          ref={textRef}
          className="flex-1 pl-0 lg:pl-20 text-center lg:text-left mt-12 lg:mt-0"
          initial="hidden"
          animate={textInView ? "visible" : "hidden"}
          variants={animations.fadeInRight}
        >
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            Transforming Vision into Reality Through Experience.
          </h2>
          <p className="text-sm md:text-base text-gray-600 leading-relaxed">
            To establish ourselves as an internationally respected engineering
            consultant, offering comprehensive and innovative solutions.
          </p>
        </motion.div>
      </div>
    </div>
  );
});

const PopularServiceHome = React.memo(() => {
  const services = useMemo(
    () => [
      {
        title: "Water Supply",
        description:
          "Designing and enhancing water supply networks for efficient distribution.",
        icon: "💧",
      },
      {
        title: "Waste Water Management",
        description: "Advanced waste water treatment and management systems.",
        icon: "🌊",
      },
      {
        title: "Civil Engineering",
        description: "Comprehensive civil engineering solutions.",
        icon: "🏗️",
      },
      {
        title: "Transportation",
        description: "Modern transportation infrastructure development.",
        icon: "🚆",
      },
      {
        title: "Solid Waste Management",
        description: "Efficient solid waste disposal and recycling solutions.",
        icon: "♻️",
      },
      {
        title: "Coastal Conservation",
        description: "Sustainable coastal development and protection.",
        icon: "🌊",
      },
    ],
    []
  );

  return (
    <div className="bg-gray-900 text-white py-8 md:py-16 px-20 md:px-28">
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="lg:flex-1 min-w-[250px] lg:pr-8 text-center lg:text-left">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Our Domain of Expertise
          </h2>
          <p className="text-gray-300 mb-6 text-sm md:text-base">
            We prioritize safe, reliable and operationally efficient designs
            tailored to your requirements.
          </p>
          <Link
            to="/ServicePage"
            className="inline-block bg-[#87212e] text-white px-6 py-3 rounded hover:bg-[#6d1a25] transition-colors text-sm md:text-base"
          >
            Explore More
          </Link>
        </div>
        <Link to="/ServicePage">
          <div className="lg:flex-[2] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="bg-gray-800 p-6 md:p-8 rounded-lg text-center border border-gray-700 hover:bg-gray-700 transition-colors"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="text-3xl md:text-4xl mb-4">{service.icon}</div>
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  {service.title}
                </h3>
                <p className="text-sm md:text-base text-gray-400">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </Link>
      </div>
    </div>
  );
});

const ServiceLayout = React.memo(() => {
  const services = useMemo(
    () => [
      {
        id: 1,
        title: "Work of Construction and Commissioning of Water Supply",
        image: WaterSupply,
        alt: "Modern house",
        hasReadMore: false,
      },
      {
        id: 2,
        title: "Survey, Design, DPR",
        image: UnderSewage,
        alt: "A-frame house",
        hasReadMore: false,
      },
      {
        id: 3,
        title: "EPC of VMC",
        image: WaterTreat,
        alt: "Water Treatment",
        hasReadMore: false,
      },
      {
        id: 4,
        title: "PMC Services for Preparation of DPR",
        image: DrainC,
        alt: "A-frame house in forest",
        hasReadMore: true,
      },
      {
        id: 5,
        title: "Designing, Constructing, Testing & Commissioning of Intake Well",
        image: Well,
        alt: "Luxury condo",
        hasReadMore: false,
      },
    ],
    []
  );

  const getLayoutClass = useCallback((index) => {
    if (index === 0) return "md:col-span-6 aspect-[16/9]";
    if (index === 1 || index === 2) return "md:col-span-3 md:aspect-[3/4]";
    if (index === 3 || index === 4) return "md:col-span-6 aspect-video";
    return "md:col-span-6 aspect-video";
  }, []);

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-medium text-gray-900 mb-4">
          Checkout More{" "}
          <span className="text-gray-600 font-normal">Projects</span>
        </h2>
        <p className="text-gray-600 text-sm md:text-base max-w-lg mx-auto">
          Join us in exploring spaces designed not just for today, but for a
          sustainable tomorrow.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
        {services.map((service, index) => (
          <ServiceCard
            key={service.id}
            service={service}
            className={getLayoutClass(index)}
          />
        ))}
      </div>
    </div>
  );
});

const ContactUsSecHome = React.memo(() => {
  return (
    <section
      className="relative py-12 md:py-24 bg-cover bg-center text-white text-center"
      style={{
        backgroundImage: `url(${Building2})`
      }}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50"></div>
      <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">
          Building today for a sustainable tomorrow.
        </h1>
        <p className="text-base md:text-lg lg:text-xl mb-6 md:mb-10">
          We believe in creating and not just in building structures. We are
          committed to live a lasting legacy, combining cutting edge technology
          and skilled resources.
        </p>
        <Link to="/Contact">
          <button className="bg-[#87212e] text-white px-6 md:px-8 py-3 md:py-4 rounded-lg text-sm md:text-lg font-semibold hover:bg-[#6d1a25] transition-colors transform hover:scale-105 duration-200">
            Contact Us
          </button>
        </Link>
      </div>
    </section>
  );
});

const BackToTopButton = React.memo(() => {
  const [isVisible, setIsVisible] = useState(false);

  const toggleVisibility = useCallback(() => {
    requestAnimationFrame(() => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    });
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    let rafId;
    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(toggleVisibility);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, [toggleVisibility]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          onClick={scrollToTop}
          className="fixed bottom-4 right-4 bg-[#87212e] text-white p-3 rounded-full shadow-lg hover:bg-[#6d1a25] transition-all duration-200 transform hover:scale-110 z-50"
          aria-label="Back to top"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </motion.button>
      )}
    </AnimatePresence>
  );
});

const Home = React.memo(() => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
    document.body.style.overflow = !isMenuOpen ? "hidden" : "unset";
  }, [isMenuOpen]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "unset";
      setIsMenuOpen(false);
    };
  }, []);

  const renderMenu = useCallback(() => (
    <div
      className={`fixed inset-0 bg-gray-900 bg-opacity-95 z-40 lg:hidden transition-transform duration-200 transform ${
        isMenuOpen ? "translate-x-0" : "translate-x-full"
      }`}
    >
      <nav className="flex flex-col items-center justify-center h-full">
        <Link
          to="/"
          className="text-white text-xl py-4 hover:text-red-500 transition-colors"
          onClick={toggleMenu}
        >
          Home
        </Link>
        <Link
          to="/Leadership"
          className="text-white text-xl py-4 hover:text-red-500 transition-colors"
          onClick={toggleMenu}
        >
          About
        </Link>
        <Link
          to="/ServicePage"
          className="text-white text-xl py-4 hover:text-red-500 transition-colors"
          onClick={toggleMenu}
        >
          Services
        </Link>
        <Link
          to="/RecentProject"
          className="text-white text-xl py-4 hover:text-red-500 transition-colors"
          onClick={toggleMenu}
        >
          Projects
        </Link>
        <Link
          to="/contact"
          className="text-white text-xl py-4 hover:text-red-500 transition-colors"
          onClick={toggleMenu}
        >
          Contact
        </Link>
      </nav>
    </div>
  ), [isMenuOpen, toggleMenu]);

  return (
    <div className="min-h-screen relative">
      <main className="relative">
        <HeroSection />
        <PreInfoHome />
        <PopularServiceHome />
        <Link to="/RecentProject">
        <ServiceLayout />
        </Link>
        <ContactUsSecHome />
      </main>
      {renderMenu()}
      <BackToTopButton />
    </div>
  );
});

export default Home;